import React, { useState } from "react";
import PropTypes from "prop-types";

import { withStyles, makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import Typography from "@material-ui/core/Typography";
import Popover from "@material-ui/core/Popover";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import MuiTableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";

import { FormatAmount } from "../../utils/Formatter";
import { ExtendedFamily } from "../../model/Charge";

const TableCell = withStyles({
  root: {
    borderBottom: "none"
  }
})(MuiTableCell);

const useStyles = makeStyles(({ palette }) => {
  return {
    root: {
      background: "rgba(0, 0, 0, 0.67)"
    },
    popover: {
      borderRadius: 0,
      bottom: "0 !important",
      left: "0 !important",
      top: "initial !important",
      width: "100%",
      maxWidth: "inherit",
      maxHeight: "50%"
    },
    toggleIcon: {
      padding: 0
    },
    tableText: {
      "& *": {
        color: palette.text.primary
      }
    },
    tableBody: {
      "& .MuiTableCell-sizeSmall": {
        padding: "2px 24px 2px 16px"
      }
    },
    closeIcon: {
      position: "absolute",
      right: 0,
      fontSize: 18,
      opacity: ".5",
      color: palette.text.primary
    }
  };
});

const SurchargePopover = ({ charge }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [anchorEl, setAnchorEl] = useState(null);
  const { fees } = charge.paymentFamilies;
  let superTextCount = 0;
  const superTextInfoArr = [];

  const open = Boolean(anchorEl);
  const id = open ? "surcharge-popover" : undefined;

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        size="small"
        color="inherit"
        aria-label="toggle-surcharge"
        aria-describedby={id}
        onClick={handleClick}
        className={classes.toggleIcon}
      >
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorReference={fullScreen ? "anchorPosition" : "anchorEl"}
        anchorPosition={{ bottom: 0 }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: fullScreen ? "bottom" : "top",
          horizontal: "center"
        }}
        classes={{
          root: fullScreen ? classes.root : null,
          paper: fullScreen ? classes.popover : null
        }}
      >
        <>
          <IconButton
            aria-label="close"
            size="small"
            className={classes.closeIcon}
            onClick={handleClose}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
          <Table size="small" className={classes.tableText}>
            <TableHead>
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography
                    variant="subtitle2"
                    style={{ marginTop: 10, marginBottom: 10 }}
                  >
                    Convenience Charges
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {fees.map(({ fee, xfam }) => {
                if (["CC", "NET_BANKING"].includes(xfam)) {
                  superTextCount += 1;
                  const superTextInfo = `${superTextCount}] ${
                    xfam === "CC"
                      ? "Amount may vary depending on the card scheme and card type"
                      : "Amount may vary from bank to bank"
                  }`;
                  superTextInfoArr.push(
                    <Typography
                      key={`${superTextCount}-info`}
                      variant="caption"
                      style={{ fontSize: 11 }}
                    >
                      {superTextInfo}
                    </Typography>
                  );
                }
                return (
                  <TableRow key={xfam}>
                    <TableCell>
                      <Typography variant="caption">
                        {`${ExtendedFamily[xfam]} `}
                        {["CC", "NET_BANKING"].includes(xfam) ? (
                          <sup>{`[${superTextCount}]`}</sup>
                        ) : null}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {`${FormatAmount(fee, charge.currency)} + GST`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              {superTextCount > 0 && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <div
                      style={{
                        marginTop: 5,
                        marginBottom: 5,
                        display: "flex",
                        flexDirection: "column"
                      }}
                    >
                      {superTextInfoArr}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </>
      </Popover>
    </>
  );
};

SurchargePopover.propTypes = {
  charge: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    due: PropTypes.number.isRequired,
    shopId: PropTypes.string.isRequired,
    shopName: PropTypes.string.isRequired,
    shopLogoId: PropTypes.string,
    orderId: PropTypes.string.isRequired,
    testMode: PropTypes.bool.isRequired,
    paymentFamilies: PropTypes.shape({
      fees: PropTypes.arrayOf(PropTypes.shape()).isRequired
    }).isRequired
  }).isRequired
};

export default SurchargePopover;
